import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { team, untilDay } from "../account.ts";
import type { LinkInfo } from "../account.ts";
import { store, useStore } from "../store.ts";
import type { SignedIn } from "../store.ts";
import { AuthPage, authMessage } from "./Login.tsx";

function useLink(token: string) {
  const [info, setInfo] = useState<LinkInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    team.check(token).then(setInfo, (err) => setError(authMessage(err)));
  }, [token]);
  return { info, error };
}

function Invalid({ error }: { error: string }) {
  return (
    <AuthPage>
      <div className="card">
        <p className="error-box">{error}</p>
        <a className="button" href="#/anmelden">
          Zur Anmeldung
        </a>
      </div>
    </AuthPage>
  );
}

/**
 * "#/einladung/<token>": the invited person creates an account (name given by the owner, user
 * name and password their own), or joins with the account this device is signed in with.
 */
export function Join({ token, onSignedIn }: { token: string; onSignedIn: (s: SignedIn) => void }) {
  useStore();
  const { info, error } = useLink(token);
  const [form, setForm] = useState({ displayName: "", email: "", username: "", password: "", repeat: "" });
  const [fresh, setFresh] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (info?.purpose === "join") setForm((f) => ({ ...f, displayName: f.displayName || info.name }));
  }, [info]);

  if (error) return <Invalid error={error} />;
  if (!info) return <AuthPage>{<p className="muted">Lädt …</p>}</AuthPage>;
  if (info.purpose !== "join") return <Invalid error="Dieser Link ist keine Einladung." />;
  const signedIn = store.session && !store.signedOut;

  const field = (key: keyof typeof form) => ({ value: form[key], onChange: (e: { target: { value: string } }) => setForm({ ...form, [key]: e.target.value }) });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setProblem(null);
    if (form.password.length < 10) return setProblem("Das Passwort braucht mindestens 10 Zeichen.");
    if (form.password !== form.repeat) return setProblem("Die beiden Passwörter sind nicht gleich.");
    setBusy(true);
    try {
      const { repeat: _repeat, ...body } = form;
      const done = await team.join(token, { ...body, username: body.username.trim() });
      onSignedIn(done);
    } catch (err) {
      setProblem(authMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function joinWithAccount() {
    setBusy(true);
    setProblem(null);
    try {
      const { workspace, workspaces } = await team.joinWithAccount(token);
      await store.choose({ user: store.session!.user, workspaces }, workspace.id);
      location.hash = "#/speisekarten";
    } catch (err) {
      setProblem(authMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthPage>
      <div className="card">
        <h2>Willkommen, {info.name}!</h2>
        <p>
          Du bist in den Betrieb <strong>{info.workspace}</strong> eingeladen. Leg dir ein Konto an, dann bist du gleich angemeldet. Der Link gilt bis {untilDay(info.expiresAt)}.
        </p>
        {signedIn && !fresh ? (
          <>
            <p>Auf diesem Gerät bist du schon angemeldet ({store.workspaceName}). Mit deinem Konto beitreten, oder ein neues anlegen?</p>
            <button className="primary wide" disabled={busy} onClick={joinWithAccount} data-testid="join-with-account">
              Mit meinem Konto beitreten
            </button>
            <button className="wide" onClick={() => setFresh(true)}>
              Neues Konto anlegen
            </button>
          </>
        ) : (
          <form noValidate onSubmit={submit}>
            <label>
              Dein Name
              <input {...field("displayName")} maxLength={40} autoComplete="nickname" data-testid="display-name" />
              <span className="hint">So nennen dich die anderen in der App.</span>
            </label>
            <label>
              E-Mail-Adresse (Pflicht)
              <input {...field("email")} type="email" required maxLength={200} autoComplete="email" placeholder="name@beispiel.de" data-testid="email" />
              <span className="hint">Für den Link, falls du dein Passwort vergisst. Nie für Werbung.</span>
            </label>
            <label>
              Benutzername
              <input {...field("username")} maxLength={64} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} data-testid="new-username" />
              <span className="hint">Zum Anmelden. Darf auch deine E-Mail-Adresse sein.</span>
            </label>
            <label>
              Passwort
              <input {...field("password")} type="password" autoComplete="new-password" data-testid="new-password" />
              <span className="hint">Mindestens 10 Zeichen.</span>
            </label>
            <label>
              Passwort wiederholen
              <input {...field("repeat")} type="password" autoComplete="new-password" data-testid="repeat-password" />
            </label>
            {problem && <p className="error-box">{problem}</p>}
            <button className="primary" disabled={busy} data-testid="join">
              {busy ? "Konto anlegen …" : "Konto anlegen"}
            </button>
          </form>
        )}
        {problem && signedIn && !fresh && <p className="error-box">{problem}</p>}
      </div>
    </AuthPage>
  );
}

/** "#/passwort/<token>": a new password through the link the owner made. */
export function Reset({ token, onSignedIn }: { token: string; onSignedIn: (s: SignedIn) => void }) {
  useStore();
  const { info, error } = useLink(token);
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  // The links come only from the owner: removing is the default (section 18 of the shared spec).
  const [removePasskeys, setRemovePasskeys] = useState(true);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (error) return <Invalid error={error} />;
  if (!info) return <AuthPage>{<p className="muted">Lädt …</p>}</AuthPage>;
  if (info.purpose !== "reset") return <Invalid error="Dieser Link setzt kein Passwort." />;
  const passkeys = info.passkeys;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setProblem(null);
    if (password.length < 10) return setProblem("Das Passwort braucht mindestens 10 Zeichen.");
    if (password !== repeat) return setProblem("Die beiden Passwörter sind nicht gleich.");
    setBusy(true);
    try {
      onSignedIn(await team.reset(token, password, passkeys > 0 && removePasskeys));
    } catch (err) {
      setProblem(authMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthPage>
      <div className="card">
        <h2>Neues Passwort</h2>
        <p>
          Für {info.name}, Benutzername <strong>{info.username}</strong>. Der Link gilt bis {untilDay(info.expiresAt)}.
        </p>
        {store.session && !store.signedOut && <p className="notice">Auf diesem Gerät ist jemand angemeldet. Nach dem Speichern bist du als {info.name} angemeldet.</p>}
        <form noValidate onSubmit={submit}>
          <input type="text" name="username" autoComplete="username" value={info.username} readOnly hidden />
          <label>
            Neues Passwort
            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" data-testid="new-password" />
            <span className="hint">Mindestens 10 Zeichen.</span>
          </label>
          <label>
            Neues Passwort wiederholen
            <input type="password" value={repeat} onChange={(e) => setRepeat(e.target.value)} autoComplete="new-password" data-testid="repeat-password" />
          </label>
          {info.passkeys > 0 && (
            <>
              <label className="check">
                <input type="checkbox" checked={removePasskeys} onChange={(e) => setRemovePasskeys(e.target.checked)} />
                Alle Passkeys entfernen ({info.passkeys})
              </label>
              <p className="hint">Wenn ein Gerät verloren ging oder jemand anderes in dein Konto kam. Passkeys auf deinen Geräten legst du danach unter „Mein Konto“ neu an.</p>
            </>
          )}
          {problem && <p className="error-box">{problem}</p>}
          <button className="primary" disabled={busy} data-testid="save-password">
            {busy ? "Passwort speichern …" : "Passwort speichern"}
          </button>
        </form>
      </div>
    </AuthPage>
  );
}
